import { Building } from "lucide-react";

const HRHero = ({ CohortForm, details }) => {
  // console.log(details);
  return (
    <section
      className="relative w-full flex items-center justify-center overflow-hidden py-10 mt-6"
      id="hero"
    >
      {/* Professional HR Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/10 via-black to-black opacity-90" />

      {/* Organizational Chart Pattern Background */}
      <div className="absolute inset-0 opacity-5">
        <svg className="w-full h-full" viewBox="0 0 800 600" fill="none">
          {/* CEO Level */}
          <rect
            x="350"
            y="50"
            width="100"
            height="40"
            rx="8"
            fill="currentColor"
            className="text-blue-400"
          />
          {/* Department Heads */}
          <rect
            x="150"
            y="150"
            width="80"
            height="30"
            rx="6"
            fill="currentColor"
            className="text-green-400"
          />
          <rect
            x="280"
            y="150"
            width="80"
            height="30"
            rx="6"
            fill="currentColor"
            className="text-purple-400"
          />
          <rect
            x="410"
            y="150"
            width="80"
            height="30"
            rx="6"
            fill="currentColor"
            className="text-orange-400"
          />
          <rect
            x="540"
            y="150"
            width="80"
            height="30"
            rx="6"
            fill="currentColor"
            className="text-pink-400"
          />
          {/* Team Members */}
          <rect
            x="120"
            y="220"
            width="60"
            height="25"
            rx="4"
            fill="currentColor"
            className="text-gray-400"
          />
          <rect
            x="200"
            y="220"
            width="60"
            height="25"
            rx="4"
            fill="currentColor"
            className="text-gray-400"
          />
          <rect
            x="250"
            y="220"
            width="60"
            height="25"
            rx="4"
            fill="currentColor"
            className="text-gray-400"
          />
          <rect
            x="330"
            y="220"
            width="60"
            height="25"
            rx="4"
            fill="currentColor"
            className="text-gray-400"
          />
          {/* Connection Lines */}
          <line
            x1="400"
            y1="90"
            x2="190"
            y2="150"
            stroke="currentColor"
            strokeWidth="1"
            className="text-gray-600"
          />
          <line
            x1="400"
            y1="90"
            x2="320"
            y2="150"
            stroke="currentColor"
            strokeWidth="1"
            className="text-gray-600"
          />
          <line
            x1="400"
            y1="90"
            x2="450"
            y2="150"
            stroke="currentColor"
            strokeWidth="1"
            className="text-gray-600"
          />
          <line
            x1="400"
            y1="90"
            x2="580"
            y2="150"
            stroke="currentColor"
            strokeWidth="1"
            className="text-gray-600"
          />
          <line
            x1="190"
            y1="180"
            x2="150"
            y2="220"
            stroke="currentColor"
            strokeWidth="1"
            className="text-gray-600"
          />
          <line
            x1="190"
            y1="180"
            x2="230"
            y2="220"
            stroke="currentColor"
            strokeWidth="1"
            className="text-gray-600"
          />
        </svg>
      </div>

      {/* Content Container */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 py-16 lg:py-8 flex flex-col lg:flex-row gap-10 lg:items-stretch">
        {/* Left Column - Hero Content */}
        <div className="w-full lg:w-1/2 flex flex-col items-center text-center space-y-6 lg:space-y-8">
          {/* Company Badge Style Header */}
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-lg bg-slate-800/50 border border-slate-600/30 backdrop-blur-sm">
            <Building className="w-5 h-5 text-blue-400" />
            <div className="text-left">
              <div className="text-xs text-gray-400 uppercase tracking-wide"></div>
              <div className="text-sm font-semibold text-white">
                Off-Campus Presents
              </div>
            </div>
          </div>

          {/* Main Headline - Corporate Style */}
          <div className="w-full max-w-8xl px-2 sm:px-6">
            <div className="bg-slate-900/50 rounded-xl p-8 border border-slate-700/50">
              <h1 className="text-4xl md:text-6xl font-bold mb-4 text-white">
                <span className="bg-gradient-to-r from-white to-gray-500 bg-clip-text text-transparent">
                  HR Fellowship
                </span>
              </h1>
              <div className="text-xs text-blue-400 uppercase tracking-wider mb-4">
                Launch Your HR Journey
              </div>
              <p className="text-xl md:text-lg text-gray-300 mb-6">
                Learn how to crack your first HR/Talent Acquisition role
              </p>
              <div className="text-sm text-amber-400 italic bg-amber-400/10 rounded-lg p-3 border border-amber-400/20">
                {
                  '(without writing "I love working with people" for the 100th time)'
                }
              </div>
            </div>
          </div>

          {/* HR Department Structure */}
          <div className="relative overflow-hidden rounded-xl bg-slate-900/30 border border-slate-600/30 p-6 max-w-3xl">
            <div className="text-center mb-4">
              <div className="text-xs text-gray-400 uppercase tracking-wider">
                Target Audience
              </div>
            </div>

            {/* Mini Org Chart */}
            <div className="flex flex-col items-center space-y-4">
              <div className="bg-blue-500/20 rounded-lg px-4 py-2 border border-blue-500/30">
                <span className="text-blue-300 font-medium">
                  🧑‍💼 Future HR Professional
                </span>
              </div>
              <div className="w-px h-4 bg-gray-600"></div>
              <div className="text-center">
                <p className="text-gray-300 text-lg">
                  For students who want to break into{" "}
                  <span className="text-white font-semibold">
                    HR the smart way
                  </span>
                  ,
                  <br />
                  not just the{" "}
                  <span className="text-red-400">buzzword way</span>.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Cohort Form */}
        <div className="w-full lg:w-1/2 flex flex-col items-center justify-center">
          <div className="w-full max-w-md mx-auto h-full flex items-center">
            <CohortForm
              price={details?.price}
              canBook={details?.canBook}
              seats_left={details?.seats_left}
              date={details?.startDate}
              id={106}
            />
          </div>
        </div>
      </div>

      {/* Bottom Gradient Overlay */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black to-transparent" />
    </section>
  );
};

export default HRHero;
